import {
  useCallback, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { MAX_GROUP_COUNT } from '~/_shared/constants/grouping.constants';
import type { SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { useTranslation } from '~/_shared/utils/hooks';
import { isNullsy } from '~/_shared/utils/typeGuards';
import { AppErrorType } from '~/appError/appErrorType.enum';
import { createAppError } from '~/store/modal/modal.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { getColumnGroupUniqueGroupsCount } from '~/store/spreadsheetData/grouping/spreadsheetData.grouping.helpers';
import { getSpreadsheetDataForRequestedGroupingColumn } from '~/store/spreadsheetData/spreadsheetData.helpers';
import { Unfiltered } from '~/store/spreadsheetData/spreadsheetData.state';

export const useGroupTypeColumnValidator = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [t] = useTranslation();
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();

  const validate = useCallback(async (spreadsheetColumn: SpreadsheetColumnId) => {
    if (isNullsy(clientId) || isNullsy(mapId)) {
      return false;
    }

    setIsLoading(true);
    try {
      const spreadsheetData = await getSpreadsheetDataForRequestedGroupingColumn(
        clientId,
        mapId,
        spreadsheetColumn
      );
      const groupCount = getColumnGroupUniqueGroupsCount(spreadsheetData, spreadsheetColumn, Unfiltered) ?? 0;

      if (groupCount > MAX_GROUP_COUNT) {
        dispatch(createAppError({
          errorTitle: t('filterTool.group.tooManyValues.solution'),
          text: t('filterTool.group.tooManyValues.description', { MAX_GROUP_COUNT }),
          title: t('Invalid Column for Grouping'),
          type: AppErrorType.General,
        }));

        return false;
      }

      return true;
    }
    catch (e) {
      console.error(e);
      return false;
    }
    finally {
      setIsLoading(false);
    }

  }, [clientId, dispatch, mapId, t]);

  return {
    validateGroupColumn: validate,
    isLoading,
  };
};
